// ClipboardCopy centralizes clipboard writes and user feedback.
//
// Website convention:
//   data-copy-url="..."       Copy/share a URL. Relative URLs and fragments are
//                              resolved against the current page location.
//   data-clipboard-text="..." Generic text copy.
//
// Keeping URL resolution here means Catalog, Article, backoffice and future
// components all use the same copy behavior and the same non-blocking toast.
class ClipboardCopy {
    constructor(translator, notifier, windowObject = globalThis.window ?? globalThis) {
        if (!translator) throw new Error('ClipboardCopy: translator is required');
        if (!notifier) throw new Error('ClipboardCopy: notifier is required');

        this.translator = translator;
        this.notifier = notifier;
        this.window = windowObject;
    }

    copy(text, { kind = 'text' } = {}) {
        const value = String(text ?? '');
        if (value === '') {
            return Promise.resolve(false);
        }

        const clipboard = this.window.navigator?.clipboard ?? globalThis.navigator?.clipboard;
        if (!clipboard?.writeText) {
            this.notifyError(kind);
            return Promise.resolve(false);
        }

        return clipboard.writeText(value)
            .then(() => {
                this.notifySuccess(kind);
                return true;
            })
            .catch((error) => {
                console.error('Failed to copy text:', error);
                this.notifyError(kind);
                return false;
            });
    }

    copyUrl(url) {
        const resolved = this.resolveUrl(url);
        if (!resolved) {
            this.notifyError('link');
            return Promise.resolve(false);
        }

        return this.copy(resolved, { kind: 'link' });
    }

    resolveUrl(url) {
        const currentUrl = this.window.location?.href;
        if (!currentUrl) {
            return null;
        }

        const candidate = String(url ?? '').trim() || currentUrl;
        try {
            return new URL(candidate, currentUrl).href;
        } catch (error) {
            return null;
        }
    }

    notifySuccess(kind) {
        const isLink = kind === 'link';
        this.notifier.success(
            this.translator.trans(isLink ? 'clipboard.linkCopied' : 'clipboard.copied'),
            {
                title: this.translator.trans(isLink ? 'clipboard.linkCopiedTitle' : 'common.success'),
                dismissMs: 2600,
            },
        );
    }

    notifyError(kind) {
        const isLink = kind === 'link';
        this.notifier.error(
            this.translator.trans(isLink ? 'clipboard.linkCopyError' : 'clipboard.copyError'),
            {
                title: this.translator.trans('common.error'),
                dismissMs: 4500,
            },
        );
    }

    getCopyRequest(element) {
        if (element.hasAttribute('data-copy-url')) {
            return {
                kind: 'link',
                value: this.resolveUrl(element.getAttribute('data-copy-url')),
            };
        }

        return {
            kind: element.getAttribute('data-clipboard-kind') || 'text',
            value: element.getAttribute('data-clipboard-text'),
        };
    }

    // One delegated handler covers initial and dynamically inserted controls.
    // It deliberately does not preventDefault(), so an Article permalink can
    // copy its share URL and still perform normal #fragment navigation.
    attach(selector = '[data-copy-url], [data-clipboard-text]') {
        const documentObject = this.window.document ?? globalThis.document;
        if (!documentObject?.addEventListener) {
            return;
        }

        documentObject.addEventListener('click', (event) => {
            const target = event.target.closest(selector);
            if (!target) {
                return;
            }

            const request = this.getCopyRequest(target);
            if (!request.value) {
                this.notifyError(request.kind);
                return;
            }

            this.copy(request.value, { kind: request.kind });
        });
    }
}

export default ClipboardCopy;
